import {PROVIDER_FLAGS} from '@Constants/index.constants.js';
import {NavigatorHelper} from './navigator-page.hlp.js';
//
/**
 * @typedef {Object} NavigatorStateConfig
 * @property {string} name - State name
 * @property {string} url - State URL pattern
 * @property {string} title - Page title
 * @property {string} [template] - Angular template
 * @property {Array} [templateProvider] - Annotated template factory, for flag-driven templates
 * @property {Object} params - URL parameters
 * @property {Function} lazyLoad - Dynamic module loader
 * @property {Object|Array} [resolve] - State resolves
 */
/**
 * Navigator state configuration
 * @type {NavigatorStateConfig}
 */
export const NavigatorState = {
	// component: NavigatorPage,
	name: 'navigator',
	url: '/navigator?status&assignee&search',
	title: 'Navigator',
	template: '<navigator-page flags-capabilities-access="$resolve.flagsCapabilitiesAccess"></navigator-page>',
	params: {
		status: {value: null, dynamic: true, type: 'query', array: true, squash: true},
		assignee: {value: null, dynamic: true, type: 'query', array: true, squash: true},
		search: {value: null, dynamic: true},
	},
	lazyLoad: $transition$ => {
		const $ocLazyLoad = $transition$.injector().get('$ocLazyLoad');
		const $log = $transition$.injector().get('$log');
		return import(
			/* webpackChunkName: "navigatorPageMod" */
			'@Containers/navigator-page/navigator-page.cmp.js'
		)
			.then(res => $ocLazyLoad.load({name: res.default}))
			.catch(err => {
				$log.error(err);
				throw err;
			});
	},
	resolve: {
		/** @ngInject */
		flagsCapabilitiesAccess: (RoutesResolveSvc, $state, $log) =>
			RoutesResolveSvc.getFlagsCapabilitiesLogAccess({fromState: 'navigator'})
				.then(data => data)
				.catch(err => {
					$log.error(err);
					$state.go('user.auth');
					throw err;
				}),
	},
};

/**
 * Navigator Details state configuration
 * @type {NavigatorStateConfig}
 */
export const NavigatorDetailsState = {
	name: 'navigator.details',
	url: '/details/:position/:eventLogId/:consultationId/:proposalId',
	title: 'Proposal Snapshot',
	params: {
		eventLogId: null,
		position: null,
		consultationId: null,
	},
	// provider flag decides which snapshot renders,
	templateProvider: [
		'providerFlags',
		providerFlags =>
			providerFlags?.[PROVIDER_FLAGS.PRESENTATION_V2]
				? '<proposal-snapshot-v2></proposal-snapshot-v2>'
				: '<proposal-snapshot></proposal-snapshot>',
	],
	lazyLoad: $transition$ => {
		const $ocLazyLoad = $transition$.injector().get('$ocLazyLoad');
		const $log = $transition$.injector().get('$log');
		const RoutesSvc = $transition$.injector().get('RoutesResolveSvc');
		return (
			RoutesSvc.getPresentationFlags({trans: $transition$})
				// a failed lookup imports the production page; the providerFlags resolve below
				// reports the failure, so it is not swallowed here
				.catch(() => ({}))
				.then(flags =>
					flags?.[PROVIDER_FLAGS.PRESENTATION_V2]
						? import(
								/* webpackChunkName: "proposalSnapshotV2Mod" */
								'@Containers/navigator-page/event-logs/proposal-snapshot-v2/proposal-snapshot-v2.cmp.js'
							)
						: import(
								/* webpackChunkName: "eventLogsPageMod" */
								'@Containers/navigator-page/event-logs/proposal-snapshot/proposal-snapshot.cmp.js'
							)
				)
				.then(res => $ocLazyLoad.load({name: res.default}))
				.catch(err => {
					$log.error(err);
					throw err;
				})
		);
	},
	resolve: [
		{
			token: 'providerFlags',
			policy: {when: 'LAZY'},
			deps: ['RoutesResolveSvc', '$transition$'],
			resolveFn: (RoutesSvc, trans) => RoutesSvc.getPresentationFlags({trans}),
		},
	],
};

/**
 * Navigator Inspector state configuration
 * @type {NavigatorStateConfig}
 */
export const NavigatorInspectorState = {
	name: 'navigator.inspector',
	url: '/inspector/:consultationId/:proposalId',
	title: 'Proposal Snapshot',
	template: '<navigator-inspector-page></navigator-inspector-page>',
	params: {consultationId: null, proposalId: null},
	lazyLoad: $transition$ => {
		const $log = $transition$.injector().get('$log');
		const $ocLazyLoad = $transition$.injector().get('$ocLazyLoad');
		return import(
			/* webpackChunkName: "navigatorInspectorPageMod" */
			'@Containers/navigator-page/navigator-inspector-page.cmp.js'
		)
			.then(m => $ocLazyLoad.load({name: m.default}))
			.catch(err => {
				$log.error(err);
				throw err;
			});
	},
};

const lazyLoadResults = {
	states: [NavigatorHelper, NavigatorDetailsState],
};

export default lazyLoadResults;
